import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, AfterViewChecked, OnDestroy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { ChatInputComponent } from '../../components/chat-input/chat-input.component';
import { ChatMessageComponent } from '../../components/chat-message/chat-message.component';
import { RagApiService, ApiError, ApiMode } from '@app/core/services/rag-api.service';
import { ChatService } from '@app/core/services/chat.service';
import { ChatMessage, RagSuggestion } from '../../models/support-chat.model';
import { Subscription, interval } from 'rxjs';
import { takeWhile, take } from 'rxjs/operators';
import { LogoutComponent } from '@app/features/logout/logout.component';
import { VodafoneIconComponent } from '@app/shared/icons/vodafone.component';
import { FeedbackService, FeedbackData } from '@app/core/services/feedback.service';
import { UserService } from '@app/core/services/user.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Declare the marked library which will be loaded via CDN
declare const marked: any;

// Add methods for markdown parsing
const parseMarkdown = (markdown: string, sanitizer: DomSanitizer): SafeHtml => {
  // Check if marked is available
  if (typeof marked !== 'undefined') {
    // Parse markdown to HTML and sanitize with Angular's sanitizer
    return sanitizer.bypassSecurityTrustHtml(marked.parse(markdown));
  }
  
  // Fallback to plain text if marked is not loaded yet
  return sanitizer.bypassSecurityTrustHtml(
    `<p>${markdown.replace(/\n/g, '<br>')}</p>`
  );
};

// Animations for the component
const supportChatAnimations = {
  fadeIn: trigger('fadeIn', [
    transition(':enter', [
      style({ opacity: 0 }),
      animate('0.3s ease-out', style({ opacity: 1 }))
    ])
  ]),
  slideFade: trigger('slideFade', [
    transition(':enter', [
      style({ opacity: 0, transform: 'translateX(100%)' }),
      animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
    ]),
    transition(':leave', [
      animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(100%)' }))
    ])
  ])
};

@Component({
  selector: 'app-support-chat',
  standalone: true,
imports: [CommonModule, ChatInputComponent, ChatMessageComponent, LogoutComponent, VodafoneIconComponent],
  template: `
     <div class="min-h-screen bg-gray-50 flex flex-col justify-between font-sans relative">
         
      <div class="max-w-6xl mx-auto w-full p-2 sm:p-4 flex flex-col flex-grow relative">
        <div class="bg-white rounded-2xl shadow-xl flex flex-col h-full overflow-hidden relative" style="min-height: 300px;">
          <!-- Top right icons container -->
          <div class="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 flex items-center gap-2">
            <app-vodafone-icon class="w-8 h-8 text-red-600"></app-vodafone-icon>
            <app-logout></app-logout>
          </div>

          <!-- Header -->
          <div class="px-4 sm:px-8 py-5 bg-gray-200 rounded-t-2xl">
            <h1 class="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Support Chat</h1>
            <p class="text-sm sm:text-base mt-2 text-gray-900 flex items-center">
              Welcome,&nbsp;<span class="font-bold">{{ userName || 'User' }}</span>!&nbsp;<span class="ml-1">👋</span>
            </p>
            <p class="text-sm mt-1 text-gray-700">We're here to help you find solutions</p>
            <p *ngIf="issueKey" class="text-sm mt-1 text-vodafone-red">
              <strong>JIRA Issue:</strong> {{issueKey}}
            </p>
          </div>

          <!-- Error Alert -->
          <div *ngIf="errorMessage" class="px-4 sm:px-8 py-4 bg-red-50 border-l-4 border-red-500">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Errore</h3>
                <div class="mt-1 text-sm text-red-700">
                  <p>{{errorMessage}}</p>
                </div>
                <div class="mt-3">
                  <button 
                    (click)="errorMessage = ''" 
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Messages -->
          <div #messageContainer class="px-4 sm:px-8 py-6 overflow-y-auto flex-1 space-y-3 sm:space-y-6 bg-gray-50">
            <app-chat-message *ngFor="let message of messages" [message]="message"></app-chat-message>

            <!-- Loading Animation -->
            <div *ngIf="loading" class="flex items-center space-x-2">
              <div class="flex space-x-1">
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-200"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-pulse animation-delay-400"></div>
              </div>
              <span class="text-gray-600 text-sm">Generating response...</span>
            </div>
          </div>

          <!-- Suggestion Section -->
          <div *ngIf="currentSuggestion" class="px-4 sm:px-8 py-4 bg-gray-100 border-t border-gray-300">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
              <button
                (click)="markSuggestionHelpful()"
                class="text-sm text-green-600 hover:text-green-700 flex items-center"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"/>
                </svg>
                This helped me
              </button>

              <button
                (click)="openTicket()"
                class="text-sm text-vodafone-red hover:text-red-700 flex items-center"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"/>
                </svg>
                I need more help
              </button>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="px-4 sm:px-8 py-6 bg-gray-100 border-t border-gray-300" [style.min-height]="inputContainerHeight + 'px'">
            <app-chat-input 
              (sendMessage)="onSendMessage($event)" 
              (heightChange)="onInputHeightChange($event)"
              [disabled]="loading"
              [feedbackGiven]="feedbackGiven">
            </app-chat-input>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .animation-delay-200 {
      animation-delay: 0.2s;
    }
    .animation-delay-400 {
      animation-delay: 0.4s;
    }
  `],
  animations: [
    supportChatAnimations.fadeIn,
    supportChatAnimations.slideFade
  ]
})
export class SupportChatComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('messageContainer') private messageContainer!: ElementRef;

  messages: ChatMessage[] = [];
  currentSuggestion: RagSuggestion | null = null;
  loading = false;
  initialQuestion: string | null = null;
  initialQuestionProcessed = false;
  issueKey: string | null = null;
  errorMessage: string = '';
  feedbackGiven = false;
  inputContainerHeight = 150; // Altezza predefinita in pixel (aumentata per adattarsi alla textarea più alta)
  userName: string = ''; // Property to store user's full name
  private pollSubscription: Subscription | null = null;
  private routeSubscription: Subscription | null = null;
  private userSubscription!: Subscription;

  constructor(
    private ragApiService: RagApiService,
    private chatService: ChatService,
    private router: Router,
    private route: ActivatedRoute,
    private feedbackService: FeedbackService,
    private userService: UserService,
    private sanitizer: DomSanitizer
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      // Check for direct preloading data
      const preloadQuestion = navigation.extras.state['question'];
      const preloadAnswer = navigation.extras.state['answer'];
      const conversationId = navigation.extras.state['conversationID'];
      
      if (preloadQuestion && conversationId) {
        // Store for processing in ngOnInit
        this.initialQuestion = preloadQuestion;
        const answer = preloadAnswer || null;
        
        // Preload the chat directly
        this.preloadChatWithData(preloadQuestion, answer, conversationId);
      } else if (preloadQuestion) {
        // Legacy handling for just question/answer
        this.initialQuestion = preloadQuestion;
        if (preloadAnswer) {
          this.addMessage('assistant', preloadAnswer);
        }
      }
    }
  }

  ngOnInit(): void {
    // Dynamically load the marked library from CDN if not already loaded
    if (typeof marked === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Load the user's name from the user service
    // Subscribe to user$ to get user data (automatically loaded from sessionStorage)
    this.userSubscription = this.userService.user$.subscribe(user => {
      if (user) {
        this.userName = user.full_name;
      }
    });
    
    // Check for ticket query parameter
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      this.issueKey = params['ticket'];
      
      // Check for direct preload parameters
      const question = params['question'];
      const answer = params['answer'];
      const conversationId = params['conversationID'];
      
      if (question && conversationId) {
        // Preload chat with provided data
        this.preloadChatWithData(question, answer, conversationId);
      } else if (this.issueKey) {
        // Legacy JIRA issue loading
        this.loading = true;
        this.chatService.loadIssueDetails(this.issueKey).subscribe({
          next: (response) => {
            console.log('Issue details loaded in component:', response);
            if (response.question) {
              this.addMessage('user', response.question);
            }
            if (response.answer) {
              this.addMessage('assistant', response.answer);
            }
            this.loading = false;
            this.initialQuestionProcessed = true;
          },
          error: (error: any) => {
            console.error('Error loading issue details:', error);
            this.loading = false;
            
            // Handle specific error cases
            if (error && error.status === 404) {
              if (error.detail === "Il ticket non esiste") {
                // Handle the specific Italian error message
                this.errorMessage = "Il ticket richiesto non esiste o non è accessibile.";
              } else {
                this.errorMessage = "The requested ticket does not exist or is not accessible.";
              }
            } else {
              // Generic error handling
              this.errorMessage = "Si è verificato un errore durante il caricamento del ticket. Riprova più tardi.";
              this.addMessage('assistant', 'Sorry, I encountered an error loading the JIRA issue. Please try again or start a new conversation.');
            }
          }
        });
      } else if (this.initialQuestion && !this.initialQuestionProcessed) {
        this.addMessage('user', this.initialQuestion);
        this.processQuestion(this.initialQuestion);
        this.initialQuestionProcessed = true;
      }
    });
  }

  preloadChatWithData(question: string, answer: string | null, conversationId: string): void {
    this.loading = true;
    this.chatService.preloadChat(question, answer, conversationId).subscribe({
      next: () => {
        if (question) {
          this.addMessage('user', question);
        }
        if (answer) {
          this.addMessage('assistant', answer);
        }
        this.loading = false;
        this.initialQuestionProcessed = true;
      },
      error: (error) => {
        console.error('Error preloading chat:', error);
        this.loading = false;
        this.errorMessage = "Si è verificato un errore durante il caricamento della conversazione. Riprova più tardi.";
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  ngOnDestroy(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  onSendMessage(content: string): void {
    if (this.loading) return;

    // Clear any error messages when the user sends a new message
    this.errorMessage = '';
    
    this.addMessage('user', content);

    const conversationId = this.ragApiService.getStoredConversationId();
    if (conversationId) {
      this.loading = true;
      this.currentSuggestion = null;
      this.ragApiService.continue(conversationId, {
        actor: 'user',
        question: content,
        history: this.messages.map(msg => ({
          question: msg.type === 'user' ? msg.content : '',
          answer: msg.type === 'assistant' ? msg.content : '',
        })),
        load_history: true,
      }, ApiMode.OPERATOR).subscribe({
        next: (response) => this.startPolling(response.promptID),
        error: (error: ApiError) => {
          console.error('Continue conversation error:', error);
          this.loading = false;
          
          if (error && error.status === 404) {
            this.errorMessage = "La conversazione non è più disponibile. Iniziamo una nuova conversazione.";
            // Reset conversation ID as it's no longer valid
            this.ragApiService.clearStoredIds();
            // Process as a new question
            this.processQuestion(content);
          } else {
            this.addMessage('assistant', 'Sorry, I encountered an error. Please try again.');
          }
        },
      });
    } else if (!this.initialQuestionProcessed) {
      this.processQuestion(content);
      this.initialQuestionProcessed = true;
    }
  }

  private processQuestion(content: string): void {
    this.loading = true;
    this.currentSuggestion = null;

    const request = {
      actor: 'user',
      question: content,
      history: []
    };

    this.ragApiService.predict(request, ApiMode.OPERATOR).subscribe({
      next: (response) => {
        const { promptID } = response;
        this.startPolling(promptID);
      },
      error: (error: ApiError) => {
        console.error('Chat error:', error);
        this.loading = false;
        
        if (error && error.detail) {
          this.errorMessage = `Errore: ${error.detail}`;
        } else {
          this.addMessage('assistant', 'Sorry, I encountered an error. Please try again.');
        }
      },
    });
  }

  private startPolling(promptId: string): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }

    let attempts = 0;
    const maxAttempts = 30;
    let responseReceived = false;

    this.pollSubscription = interval(2000)
      .pipe(takeWhile(() => attempts < maxAttempts && !responseReceived, true))
      .subscribe(() => {
        attempts++;
        this.ragApiService.pollResponse(promptId, ApiMode.OPERATOR).subscribe({
          next: (response) => {
            if (response.output && !responseReceived) {
              responseReceived = true;
              this.pollSubscription?.unsubscribe();
              this.addMessage('assistant', response.output);
              this.currentSuggestion = {
                content: response.output,
                confidence: 1.0,
                helpful: false,
              };
              this.loading = false;
            }
          },
          error: (error) => {
            if (attempts >= maxAttempts) {
              responseReceived = true;
              this.pollSubscription?.unsubscribe();
              this.loading = false;
              
              if (error && error.detail) {
                this.errorMessage = `Errore nel recupero della risposta: ${error.detail}`;
              } else {
                this.addMessage('assistant', 'Sorry, I could not retrieve the response. Please try again.');
              }
            }
          },
        });
      });
  }

  addMessage(type: 'user' | 'assistant', content: string, solutionLink?: string): void {
    const id = `msg-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const timestamp = new Date();

    // For assistant messages, parse the markdown
    let htmlContent = undefined;
    if (type === 'assistant') {
      htmlContent = parseMarkdown(content, this.sanitizer);
    }

    this.messages.push({
      id,
      type,
      content,
      timestamp,
      solutionLink,
      htmlContent
    });

    this.scrollToBottom();
  }

  markSuggestionHelpful(): void {
    if (!this.currentSuggestion) return;
      
    // Get the last user and system messages for feedback
    const lastUserMessage = this.findLastUserMessage();
    const lastBotMessage = this.findLastBotMessage();
    
    if (!lastUserMessage || !lastBotMessage || this.feedbackService.isSubmitting()) return;
    
    // Mark as helpful in UI and set feedback given
    this.currentSuggestion.helpful = true;
    this.feedbackGiven = true;
    
    const feedbackData: FeedbackData = {
      history: [
        {
          question: lastUserMessage.content,
          answer: lastBotMessage.content,
          timestamp: new Date().toISOString()
        }
      ],
      satisfied: true,
      duration: 0, // We don't track duration in this component
      subject: lastUserMessage.content.substring(0, 50) + (lastUserMessage.content.length > 50 ? '...' : ''),
      description: lastUserMessage.content,
      priority: this.feedbackService.FIXED_PRIORITY
    };
    
    // Subscribe to isSubmitting$ state to update UI when feedback is submitted
    this.feedbackService.isSubmitting$.pipe(
      take(2) // Take exactly two values - initial (false) and after submission (true)
    ).subscribe(isSubmitting => {
      // No UI updates needed - this prevents chat from becoming disabled
      console.log('Feedback submission state:', isSubmitting);
    });
    
    this.feedbackService.submitFeedback(feedbackData).subscribe({
      next: () => {
        console.log('Suggestion feedback submitted as helpful');
        // Remove the feedback buttons but keep chat enabled
        this.currentSuggestion = null;
      },
      error: (error) => console.error('Error submitting helpful feedback', error)
    });
  }

  openTicket(): void {
    // Get the last user and system messages for feedback
    const lastUserMessage = this.findLastUserMessage();
    const lastBotMessage = this.findLastBotMessage();
    
    if (!lastUserMessage || !lastBotMessage || this.feedbackService.isSubmitting()) return;
    
    // Set feedback given
    this.feedbackGiven = true;
    
    const feedbackData: FeedbackData = {
      history: [
        {
          question: lastUserMessage.content,
          answer: lastBotMessage.content,
          timestamp: new Date().toISOString()
        }
      ],
      satisfied: false,
      duration: 0, // We don't track duration in this component
      subject: lastUserMessage.content.substring(0, 50) + (lastUserMessage.content.length > 50 ? '...' : ''),
      description: lastUserMessage.content,
      priority: this.feedbackService.FIXED_PRIORITY
    };
    
    this.feedbackService.submitFeedback(feedbackData).subscribe({
      next: () => {
        console.log('Feedback submitted as not helpful');
        // Remove the feedback buttons but keep chat enabled
        this.currentSuggestion = null;
      },
      error: (error) => console.error('Error submitting not helpful feedback', error)
    });
  }

  // Helper methods to find most recent messages
  private findLastUserMessage(): ChatMessage | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].type === 'user') {
        return this.messages[i];
      }
    }
    return null;
  }

  private findLastBotMessage(): ChatMessage | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].type === 'assistant') {
        return this.messages[i];
      }
    }
    return null;
  }

  private scrollToBottom(): void {
    try {
      // Use any method to find the container in the DOM
      // This is simpler and should work with the template
      setTimeout(() => {
        const messageContainer = document.querySelector('.px-8.py-6.overflow-y-auto.flex-1.space-y-6.bg-gray-50');
        if (messageContainer) {
          messageContainer.scrollTop = messageContainer.scrollHeight;
        }
      }, 0);
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }
  
  // Gestisce i cambiamenti di altezza del componente di input
  onInputHeightChange(height: number): void {
    // Aggiungiamo un piccolo margine e garantiamo un'altezza minima
    this.inputContainerHeight = Math.max(height + 20, 150); // Garantisce che il container sia almeno 150px di altezza
  }
}